import React, { useState, useEffect, useCallback } from 'react'
import { MessageCircle, Copy, Check } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { crearSolicitudConsentimiento } from '../../lib/supabase'
import { useOrganization } from '../../contexts/OrganizationContext'

// Construye la URL pública de firma y el enlace wa.me para compartir manualmente.
// Usa REACT_APP_PUBLIC_URL (dominio canónico) si está definida; si no, el origen actual.
const buildSignUrl = (token) => {
  const base = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, '')
  return `${base}/consentimiento/${token}`
}

const buildWaMeUrl = (telefono, texto) => {
  const digits = String(telefono || '').replace(/\D/g, '')
  const phone = digits.length === 10 ? `52${digits}` : digits // MX por defecto
  const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/'
  return `${base}?text=${encodeURIComponent(texto)}`
}

const SolicitarConsentimientoDialog = ({ open, onOpenChange, jugadorId = null, familiaId = null, tutorTelefono = null, tituloDestino = '', existingToken = null, onSolicitado = null }) => {
  const { org } = useOrganization()
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  const crear = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error } = await crearSolicitudConsentimiento({ jugadorId, familiaId })
    setLoading(false)
    if (error || !data?.token) {
      // El servicio explica QUE falta y DONDE arreglarlo (p. ej. "completa domicilio,
      // correo para derechos ARCO en Configuracion de la organizacion"). Tragarse ese
      // mensaje y decir "intenta de nuevo" es el peor consejo posible: reintentar no
      // va a funcionar nunca, y el admin no tiene forma de saber que le falta.
      setError(error?.message || 'No se pudo generar la solicitud. Intenta de nuevo.')
      return
    }
    setToken(data.token)
    onSolicitado?.(data.token)
  }, [jugadorId, familiaId, onSolicitado])

  useEffect(() => {
    if (open) {
      setCopied(false)
      // Recordatorio: reutiliza el token existente (no crea otra solicitud).
      if (existingToken) {
        setToken(existingToken)
      } else {
        setToken(null)
        crear()
      }
    }
  }, [open, crear, existingToken])

  const signUrl = token ? buildSignUrl(token) : ''
  const mensaje = `Hola. Para completar el registro${tituloDestino ? ` de ${tituloDestino}` : ''} en ${org?.name || 'el club'}, por favor firma el consentimiento aquí: ${signUrl}`
  const waUrl = token ? buildWaMeUrl(tutorTelefono, mensaje) : ''

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(signUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* noop */
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar consentimiento</DialogTitle>
          <DialogDescription>
            Comparte este enlace con el tutor para que firme. Un solo enlace cubre a los
            hermanos de la misma familia.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-6 text-center text-sm text-muted-foreground">Generando enlace…</div>
        )}
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        {token && !loading && (
          <div className="min-w-0 space-y-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Enlace de firma</p>
              <div className="flex min-w-0 items-center gap-1 rounded-lg border bg-muted/50 py-1 pl-3 pr-1">
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground/70">{signUrl}</span>
                <Button variant="ghost" size="sm" className="h-8 shrink-0 gap-1.5" onClick={copiar}>
                  {copied
                    ? <><Check className="h-3.5 w-3.5 text-green-600" /> Copiado</>
                    : <><Copy className="h-3.5 w-3.5" /> Copiar</>}
                </Button>
              </div>
            </div>

            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="w-full bg-[#25D366] hover:bg-[#1eb457] text-white">
                <MessageCircle className="h-4 w-4 mr-2" />
                Compartir por WhatsApp
              </Button>
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SolicitarConsentimientoDialog
